import { useSyncExternalStore } from 'react';

/**
 * A VEVOSZOLGALATI LEBEGO ABLAK (docs/20-vevoszolgalat.html).
 *
 * A bongeszo Document Picture-in-Picture ablaka: mindig minden mas ablak (a tavoli
 * asztal) FOLOTT marad, es tetszoleges HTML-t mutathat. Szabalyai:
 *
 *  - csak asztali Chrome/Edge tudja;
 *  - CSAK felhasznaloi kattintas/billentyu utan nyithato (kattintas nelkul a bongeszo
 *    elutasitja) - ezert kell, hogy a hivas ELOTT mar nyitva legyen;
 *  - az oldal ujratoltese bezarja.
 *
 * A tartalmat a fo oldal React-portalja rajzolja bele (igy ugyanazt a kontextust
 * - hivas, felhasznalo, lekerdezesek - latja, nincs masodik kapcsolat). Ez a modul
 * csak az ablak eletciklusat kezeli.
 */

interface PipApi {
  window: Window | null;
  requestWindow(options?: {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
  }): Promise<Window>;
}

const api = (): PipApi | null =>
  typeof window === 'undefined'
    ? null
    : ((window as unknown as { documentPictureInPicture?: PipApi }).documentPictureInPicture ??
      null);

export const pipIsSupported = (): boolean => api() !== null;

/** Az ablak mereteit a hivas elott jegyezzuk meg: a kovetkezo megnyitas ugyanekkora legyen. */
const SIZE_KEY = 'avslack.service-pip.size';
const DEFAULT_SIZE = { width: 420, height: 300 };

function rememberedSize(): { width: number; height: number } {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (typeof parsed.width === 'number' && typeof parsed.height === 'number') {
        return {
          width: Math.min(Math.max(parsed.width, 320), 900),
          height: Math.min(Math.max(parsed.height, 160), 900),
        };
      }
    }
  } catch {
    // privat mod vagy serult ertek: az alapmeret
  }
  return DEFAULT_SIZE;
}

// ─── Allapot ──────────────────────────────────────────────────────────────────

let current: { win: Window; container: HTMLElement } | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** A lebego ablak tartalmi gyokere (ide rajzol a portal), vagy `null`, ha nincs nyitva. */
export function usePipContainer(): HTMLElement | null {
  return useSyncExternalStore(
    subscribe,
    () => current?.container ?? null,
    () => null,
  );
}

export const getPipWindow = (): Window | null => current?.win ?? null;

// ─── Stilus- es tema-atvetel ──────────────────────────────────────────────────

/** A fo oldal stiluslapjai a lebego ablakba is: ugyanaz a megjelenes, ugyanazok a valtozok. */
function copyStyles(target: Document): void {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      if (sheet.href) {
        const link = target.createElement('link');
        link.rel = 'stylesheet';
        // Abszolut cim: a lebego ablak sajat dokumentuma nem a fo oldal cimen all.
        link.href = new URL(sheet.href, document.baseURI).href;
        target.head.appendChild(link);
      } else {
        const style = target.createElement('style');
        style.textContent = Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
        target.head.appendChild(style);
      }
    } catch {
      // Idegen eredetu stiluslap szabalyai nem olvashatok - nincs mit atvenni.
    }
  }
  // A dev-szerver (Vite) a stilust <style> elemekkel is csatolja: ezeket mar atvettuk a
  // `styleSheets`-bol, de az utolag beszurtakat nem - elegendo a nyitaskori allapot.
}

/** A tema (sotet/vilagos) kovesse a fo oldalet. */
function mirrorTheme(target: Document): () => void {
  const sync = () => {
    target.documentElement.className = document.documentElement.className;
    for (const attribute of ['data-theme', 'data-scheme', 'lang']) {
      const value = document.documentElement.getAttribute(attribute);
      if (value === null) target.documentElement.removeAttribute(attribute);
      else target.documentElement.setAttribute(attribute, value);
    }
    target.documentElement.style.cssText = document.documentElement.style.cssText;
  };
  sync();
  const observer = new MutationObserver(sync);
  observer.observe(document.documentElement, { attributes: true });
  return () => observer.disconnect();
}

// ─── Megnyitas / bezaras ──────────────────────────────────────────────────────

/**
 * Megnyitja a lebego ablakot. KATTINTASBOL vagy billentyuleutesbol kell hivni
 * (felhasznaloi aktivacio nelkul a bongeszo elutasitja). `null`-t ad, ha nem sikerult.
 */
export async function openServicePip(): Promise<Window | null> {
  const pip = api();
  if (!pip) return null;
  if (current && !current.win.closed) return current.win;

  let win: Window;
  try {
    win = await pip.requestWindow({ ...rememberedSize(), disallowReturnToOpener: true });
  } catch {
    // Nincs aktivacio (vagy a szabalyzat tiltja): a hivo a bannerrel jelez.
    return null;
  }

  copyStyles(win.document);
  win.document.title = 'Vevőszolgálat';
  // A tartalom magassagat a lebego ablak adja; a gorgetes a panel dolga.
  const reset = win.document.createElement('style');
  reset.textContent =
    'html,body{height:100%;margin:0;overflow:hidden}#service-root{height:100%;overflow:auto}';
  win.document.head.appendChild(reset);
  const stopTheme = mirrorTheme(win.document);

  const container = win.document.createElement('div');
  container.id = 'service-root';
  win.document.body.appendChild(container);

  const remember = () => {
    // Osszecsukva a fejlec-magassagot NEM jegyezzuk meg: a kovetkezo nyitas teljes meretu legyen.
    if (expandedHeight !== null) return;
    try {
      localStorage.setItem(
        SIZE_KEY,
        JSON.stringify({ width: win.innerWidth, height: win.innerHeight }),
      );
    } catch {
      // a meret megjegyzese kenyelem, nem funkcio
    }
  };
  win.addEventListener('resize', remember);

  win.addEventListener('pagehide', () => {
    stopTheme();
    if (current?.win === win) {
      current = null;
      expandedHeight = null;
    }
    notify();
  });

  current = { win, container };
  notify();
  return win;
}

export function closeServicePip(): void {
  const win = current?.win;
  current = null;
  expandedHeight = null;
  notify();
  try {
    win?.close();
  } catch {
    // mar bezarult
  }
}

/** A lebego ablak atmeretezese - csak felhasznaloi kattintasbol enged a bongeszo. */
export function resizeServicePip(height: number, width?: number): void {
  const win = current?.win;
  if (!win) return;
  try {
    win.resizeTo(width ?? win.outerWidth, Math.round(height));
  } catch {
    // aktivacio nelkul elutasitja - a panel gorgetheto marad
  }
}

// ─── Osszecsukas ──────────────────────────────────────────────────────────────

/**
 * Az osszecsukott panel belso magassaga: egyetlen sor (a `ServicePanel` osszecsukott
 * nezete pontosan ekkora). A bongeszo ennel nagyobb minimumot is eloirhat - akkor
 * alul ures csik marad, de a sor ugyanugy hasznalhato.
 */
export const COLLAPSED_HEIGHT = 44;

/** Osszecsukaskor a teljes magassag (ide nyilik vissza); `null`, ha nincs osszecsukva. */
let expandedHeight: number | null = null;

export function usePipCollapsed(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => expandedHeight !== null,
    () => false,
  );
}

/**
 * Osszecsukja (csak a fejlec-sor latszik) vagy visszanyitja a lebego ablakot.
 * KATTINTASBOL hivando: atmeretezni csak felhasznaloi aktivacioval enged a bongeszo.
 * Szinkron meretez (nem `requestAnimationFrame`-ben): a fo ablak ilyenkor gyakran
 * a hatterben van, ott a rAF nem fut le.
 */
export function setServicePipCollapsed(collapsed: boolean): void {
  const win = current?.win;
  if (!win || collapsed === (expandedHeight !== null)) return;
  try {
    if (collapsed) {
      expandedHeight = win.outerHeight;
      win.resizeTo(win.outerWidth, COLLAPSED_HEIGHT + (win.outerHeight - win.innerHeight));
    } else {
      const height = expandedHeight ?? rememberedSize().height;
      expandedHeight = null;
      win.resizeTo(win.outerWidth, height);
    }
  } catch {
    // aktivacio nelkul elutasitja - az allapot ettol meg valt, a tartalom igazodik
  }
  notify();
}
